import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

// import Select from "react-select";
// import { Check } from "feather-icons-react/build/IconComponents";
import PosModals from "../../core/modals/pos-modal/posModals";
import { fetchProductList, getProductById } from "../../api/productApi";
import { getAllCategories } from "../../api/categoryApi";
import PropTypes from "prop-types";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// import { isDemoActive } from "../../api/client";
import { mockPos } from "../../api/mock/pos";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import {
  AlertCircle,
  Instagram,
  Facebook,
  MessageCircle,
  Music2,
  Ghost,
  CheckCircle2,
  ExternalLink,
  FileSpreadsheet,
  Printer,
  Search,
  X,
} from "lucide-react";

import { getPosUserSocialLinks } from "../../api/profileApi";
import { getPosOverviewReport } from "../../api/transactionReportApi";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- image URL helpers ---
const isHttpUrl = (v) => typeof v === "string" && /^https?:\/\//i.test(v);
const looksLikeCatalog = (v) =>
  typeof v === "string" && /^\/?(catalog|catelog)\//i.test(v);
const normalizeKey = (s = "") => {
  let v = String(s)
    .trim()
    .replace(/^\/+/, "")
    .replace(/^catelog\//i, "catalog/");
  if (!/^catalog\//i.test(v)) v = `catalog/${v.split("/").pop()}`;
  return v;
};
const toPublicUrl = (v) => {
  if (!v) return "";
  if (isHttpUrl(v)) return v; // already a full URL
  if (looksLikeCatalog(v)) {
    const base = (
      process.env.REACT_APP_API_IMAGE_BASE_URL ||
      "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
    ).replace(/\/+$/, "");
    return `${base}/${normalizeKey(v)}`;
  }
  return v; // leave plain filenames (used for local assets) as-is
};

//Helper to detect "no store" 403 errors
const noStore403 = (err) => {
  const msg = err?.response?.data?.message || err?.message || "";
  const code = err?.response?.status;
  return (
    code === 403 && /no\s*store/i.test(msg) // matches "No storeId on user; access denied"
  );
};

const DEFAULT_REPORT_LIMIT = 100;
const DEFAULT_REPORT_TYPE = "cash";
const MAX_PDF_ORDERS = 25;

const tr = (key) =>
  typeof window !== "undefined" && window.translate
    ? window.translate(key)
    : key;
const POS_AVAILABILITY_EVENT = "pos:product-availability";

const emitPosAvailability = (payload) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(POS_AVAILABILITY_EVENT, {
      detail: payload,
    }),
  );
};

const syncAvailabilityWithCart = (items = []) => {
  const ids = new Set();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const selectedId = String(item?._id || "").trim();
    const baseId = String(item?.baseProductId || "").trim();
    if (selectedId) ids.add(selectedId);
    if (baseId) ids.add(baseId);
  });
  emitPosAvailability({
    kind: "cart-sync",
    productIds: Array.from(ids),
  });
};

const toYmd = (dateLike) => {
  const date = new Date(dateLike);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toAmount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const toInt = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : 0;
};

const normalizeName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.en || value.ar || "";
  return String(value);
};

const getItemTaxRate = (item = {}) => {
  const candidates = [
    item?.taxValue,
    item?.tax,
    item?.taxRate,
    item?.tax_type,
    item?.taxType,
  ];

  for (const raw of candidates) {
    const parsed = Number.parseFloat(raw);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }

  return 0;
};

const getItemDiscountValue = (item = {}) => {
  const candidates = [
    item?.discountValue,
    item?.discount,
    item?.discountRate,
    item?.discount_rate,
    item?.pricing?.discountValue,
  ];

  for (const raw of candidates) {
    const parsed = Number.parseFloat(raw);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }

  return 0;
};

const getItemDiscountAmount = (item = {}) => {
  const unitPrice = Number(item?.Price ?? item?.price ?? 0);
  const quantity = Number(item?.quantity ?? 0);
  const lineTotal = unitPrice * quantity;
  const discountValue = getItemDiscountValue(item);

  if (!Number.isFinite(lineTotal) || lineTotal <= 0 || discountValue <= 0) {
    return 0;
  }

  const discountType = String(item?.discountType || "")
    .trim()
    .toLowerCase();

  if (["cash", "flat", "fixed", "amount", "value"].some((k) => discountType.includes(k))) {
    return discountValue * quantity;
  }

  if (["percentage", "percent", "%"].some((k) => discountType.includes(k))) {
    return (discountValue / 100) * lineTotal;
  }

  // Fallback for missing/unknown discount type: keep previous POS behavior.
  if (discountValue <= 100) return (discountValue / 100) * lineTotal;
  return discountValue * quantity;
};

const normalizeSplitMethod = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "";
  if (["card", "credit", "credit_card", "debit", "debit_card"].includes(normalized)) {
    return "card";
  }
  if (["cash"].includes(normalized)) return "cash";
  return normalized;
};

const POS_TX_SUMMARY_STORAGE_KEY = "posTransactionSummaries";
const POS_TX_SUMMARY_MAX_ITEMS = 200;

const toFiniteNumberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const readPosTransactionSummaries = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(POS_TX_SUMMARY_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writePosTransactionSummaries = (summaries) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(POS_TX_SUMMARY_STORAGE_KEY, JSON.stringify(summaries));
  } catch {
    // Ignore storage quota / parsing errors.
  }
};

const cachePosTransactionSummary = (tx = {}) => {
  if (typeof window === "undefined") return;
  const orderKey = String(tx.orderId || tx._id || "").trim();
  if (!orderKey) return;

  const summary = {
    orderId: orderKey,
    subtotalAmount: toFiniteNumberOrNull(tx.subtotalAmount) ?? 0,
    taxAmount: toFiniteNumberOrNull(tx.taxAmount) ?? 0,
    discountAmount: toFiniteNumberOrNull(tx.discountAmount) ?? 0,
    shippingAmount: toFiniteNumberOrNull(tx.shippingAmount) ?? 0,
    totalAmount: toFiniteNumberOrNull(tx.totalAmount) ?? 0,
    updatedAt: Date.now(),
  };

  const summaries = readPosTransactionSummaries();
  summaries[orderKey] = summary;

  const keys = Object.keys(summaries);
  if (keys.length > POS_TX_SUMMARY_MAX_ITEMS) {
    keys
      .sort((a, b) => Number(summaries[a]?.updatedAt || 0) - Number(summaries[b]?.updatedAt || 0))
      .slice(0, keys.length - POS_TX_SUMMARY_MAX_ITEMS)
      .forEach((key) => delete summaries[key]);
  }
  writePosTransactionSummaries(summaries);
};

const getCachedPosTransactionSummary = (tx = {}) => {
  const orderKey = String(tx.orderId || tx._id || "").trim();
  if (!orderKey) return null;
  const summaries = readPosTransactionSummaries();
  return summaries[orderKey] || null;
};

const parseSplitPaymentsFromNote = (note) => {
  const text = String(note || "");
  const match = text.match(/\[split:([^\]]+)\]/i);
  if (!match?.[1]) return [];
  return match[1]
    .split("|")
    .map((part) => part.trim())
    .map((part) => {
      const [methodRaw, amountRaw] = part.split(":");
      const method = normalizeSplitMethod(methodRaw);
      const amount = Number(amountRaw);
      if (!method || !Number.isFinite(amount) || amount <= 0) return null;
      return { method, amount };
    })
    .filter(Boolean);
};

const getSplitPaymentsFromTx = (tx) => {
  const fromPayload = Array.isArray(tx?.splitPayments)
    ? tx.splitPayments
        .map((payment) => {
          const method = normalizeSplitMethod(payment?.method);
          const amount = Number(payment?.amount);
          if (!method || !Number.isFinite(amount) || amount <= 0) return null;
          return { method, amount };
        })
        .filter(Boolean)
    : [];
  if (fromPayload.length > 0) return fromPayload;
  return parseSplitPaymentsFromNote(tx?.paymentNote);
};

const getDefaultReportRange = () => {
  const to = toYmd(new Date());
  const firstDayOfMonth = new Date();
  firstDayOfMonth.setDate(1);
  return {
    from: toYmd(firstDayOfMonth),
    to,
  };
};

const buildReportParams = ({ userId, from, to, limit, type } = {}) => {
  const fallbackRange = getDefaultReportRange();
  const parsedLimit = Number(limit);
  const isNoLimit =
    limit === "all" || limit === "" || limit === null || limit === undefined;
  const normalizedType = type === "all" ? undefined : type;
  return {
    from: from || fallbackRange.from,
    to: to || fallbackRange.to,
    userId: userId || undefined,
    limit:
      isNoLimit
        ? undefined
        : Number.isFinite(parsedLimit) && parsedLimit > 0
        ? parsedLimit
        : DEFAULT_REPORT_LIMIT,
    type: normalizedType || DEFAULT_REPORT_TYPE,
  };
};

const downloadOverviewExcel = (report, params) => {
  const metricKey = tr("Metric");
  const valueKey = tr("Value");
  const summary = report?.summary || {};
  const summaryRows = [
    {
      [metricKey]: tr("Total Transactions"),
      [valueKey]: toInt(summary.totalTransactions),
    },
    {
      [metricKey]: tr("Total Sales Amount"),
      [valueKey]: Number(toAmount(summary.totalSalesAmount)),
    },
    {
      [metricKey]: tr("Total Received Amount"),
      [valueKey]: Number(toAmount(summary.totalReceivedAmount)),
    },
    {
      [metricKey]: tr("Total Items Sold"),
      [valueKey]: toInt(summary.totalItemsSold),
    },
    { [metricKey]: tr("From"), [valueKey]: params.from || "-" },
    { [metricKey]: tr("To"), [valueKey]: params.to || "-" },
    { [metricKey]: tr("Payment Type"), [valueKey]: params.type || tr("All") },
    { [metricKey]: tr("Limits"), [valueKey]: params.limit ?? tr("All") },
  ];

  const orderRows = (report?.orders || []).map((order, index) => {
    const sellerName = [
      normalizeName(order?.seller?.firstName),
      normalizeName(order?.seller?.lastName),
    ]
      .join(" ")
      .trim();
    return {
      Sr: index + 1,
      OrderId: order?.orderId || "-",
      TransactionId: order?.transactionId || "-",
      Type: order?.type || "-",
      TotalAmount: Number(toAmount(order?.totalAmount)),
      ReceivedAmount: Number(toAmount(order?.receivedAmount)),
      ItemsSold: toInt(order?.itemsSold),
      Seller: sellerName || order?.seller?.username || "-",
      CreatedAt: order?.createdAt
        ? new Date(order.createdAt).toLocaleString()
        : "-",
    };
  });

  const userRows = (report?.userSales || []).map((user, index) => ({
    Sr: index + 1,
    UserId: user?.userId || "-",
    Name:
      [normalizeName(user?.firstName), normalizeName(user?.lastName)]
        .join(" ")
        .trim() || "-",
    Username: user?.username || "-",
    Role: user?.role || "-",
    TotalTransactions: toInt(user?.totalTransactions),
    TotalSalesAmount: Number(toAmount(user?.totalSalesAmount)),
    TotalItemsSold: toInt(user?.totalItemsSold),
  }));

  const productRows = (report?.productSales || []).map((product, index) => ({
    Sr: index + 1,
    ProductId: product?.productId || "-",
    Name: product?.name || "-",
    TotalQuantitySold: toInt(product?.totalQuantitySold),
    TotalSalesAmount: Number(toAmount(product?.totalSalesAmount)),
    TransactionCount: toInt(product?.transactionCount),
    SellerCount: toInt(product?.sellerCount),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(summaryRows),
    tr("Summary"),
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(orderRows),
    tr("Orders"),
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(userRows),
    tr("UserSales"),
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(productRows),
    tr("ProductSales"),
  );
  XLSX.writeFile(wb, `pos_overview_${params.from}_to_${params.to}.xlsx`);
};

const downloadOverviewPdf = (report, params) => {
  const colors = {
    coral: [254, 159, 67],
    coralLight: [255, 243, 230],
    coralMid: [254, 219, 179],
    textDark: [26, 26, 46],
    textMid: [85, 85, 85],
    textLight: [130, 130, 145],
    border: [240, 228, 227],
  };

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const generatedAt = new Date().toLocaleString();

  const summary = report?.summary || {};
  const summaryCards = [
    {
      label: tr("Total Transactions"),
      value: String(toInt(summary.totalTransactions)),
    },
    {
      label: tr("Total Sales"),
      value: `$${toAmount(summary.totalSalesAmount)}`,
    },
    {
      label: tr("Total Received"),
      value: `$${toAmount(summary.totalReceivedAmount)}`,
    },
    { label: tr("Items Sold"), value: String(toInt(summary.totalItemsSold)) },
  ];

  doc.setFillColor(...colors.coral);
  doc.rect(0, 0, pageWidth, 86, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(tr("POS Selling Overview Report"), marginX, 38);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(
    `${tr("Range")}: ${params.from || "-"} - ${params.to || "-"}   |   ${tr("Type")}: ${params.type || tr("All")}   |   ${tr("Limits")}: ${params.limit ?? tr("All")}`,
    marginX,
    58,
  );
  doc.text(`${tr("Generated")}: ${generatedAt}`, marginX, 74);

  let cursorY = 112;
  const cardGap = 12;
  const cardWidth = (pageWidth - marginX * 2 - cardGap) / 2;
  const cardHeight = 70;

  summaryCards.forEach((card, index) => {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = marginX + col * (cardWidth + cardGap);
    const y = cursorY + row * (cardHeight + cardGap);
    doc.setFillColor(...colors.coralLight);
    doc.setDrawColor(...colors.coralMid);
    doc.roundedRect(x, y, cardWidth, cardHeight, 8, 8, "FD");
    doc.setTextColor(...colors.textMid);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(card.label, x + 12, y + 24);
    doc.setTextColor(...colors.textDark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(card.value, x + 12, y + 50);
  });

  cursorY += cardHeight * 2 + cardGap + 24;
  doc.setTextColor(...colors.textDark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(tr("Recent Orders"), marginX, cursorY);
  cursorY += 10;

  const orders = report?.orders || [];
  const tableRows = orders.length
    ? orders.slice(0, MAX_PDF_ORDERS).map((order, idx) => {
        const sellerName = [
          normalizeName(order?.seller?.firstName),
          normalizeName(order?.seller?.lastName),
        ]
          .join(" ")
          .trim();
        return [
          idx + 1,
          order?.orderId || order?.transactionId || "-",
          order?.type || "-",
          sellerName || order?.seller?.username || "-",
          toInt(order?.itemsSold),
          `$${toAmount(order?.totalAmount)}`,
          `$${toAmount(order?.receivedAmount)}`,
          order?.createdAt ? new Date(order.createdAt).toLocaleString() : "-",
        ];
      })
    : [
        [
          "-",
          tr("No orders found for this selected range."),
          "-",
          "-",
          "-",
          "-",
          "-",
          "-",
        ],
      ];

  autoTable(doc, {
    startY: cursorY + 8,
    margin: { left: marginX, right: marginX },
    head: [
      [
        "#",
        tr("Order / Txn"),
        tr("Type"),
        tr("Seller"),
        tr("Items"),
        tr("Total"),
        tr("Received"),
        tr("Created"),
      ],
    ],
    body: tableRows,
    theme: "grid",
    styles: {
      fontSize: 9,
      cellPadding: 6,
      textColor: colors.textDark,
      lineColor: colors.border,
      lineWidth: 0.5,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: colors.coral,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      lineColor: colors.coral,
    },
    alternateRowStyles: { fillColor: [253, 248, 247] },
    columnStyles: {
      0: { halign: "center", cellWidth: 24 },
      1: { cellWidth: 82 },
      2: { cellWidth: 46 },
      3: { cellWidth: 88 },
      4: { halign: "center", cellWidth: 34 },
      5: { halign: "right", cellWidth: 52 },
      6: { halign: "right", cellWidth: 60 },
      7: { cellWidth: 110 },
    },
    didDrawPage: () => {
      doc.setDrawColor(...colors.border);
      doc.line(marginX, pageHeight - 32, pageWidth - marginX, pageHeight - 32);
      doc.setFontSize(9);
      doc.setTextColor(...colors.textLight);
      doc.text(tr("Hamilton POS - Overview Report"), marginX, pageHeight - 16);
    },
  });

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setFontSize(9);
    doc.setTextColor(...colors.textLight);
    doc.text(
      `${tr("Page")} ${page} ${tr("of")} ${totalPages}`,
      pageWidth - marginX,
      pageHeight - 16,
      { align: "right" },
    );
  }
  doc.save(`pos_overview_${params.from}_to_${params.to}.pdf`);
};

const Pos = () => {
  const [activeTab, setActiveTab] = useState("all");
  //   const [showAlert, setShowAlert] = useState(true);
  const [demo, setDemo] = useState(false);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  // const [productCache, setProductCache] = useState({});

  const [receivedAmount, setReceivedAmount] = useState("");
  const [payingAmount, setPayingAmount] = useState(0);
  const [change, setChange] = useState("0.00");
  const [paymentType, setPaymentType] = useState({
    value: "cash",
    label: "Cash",
  });

  const [paymentReceiver, setPaymentReceiver] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [saleNote, setSaleNote] = useState("");
  const [staffNote, setStaffNote] = useState("");

  const [lastTransaction, setLastTransaction] = useState(null);
  const [invoiceLang, setInvoiceLang] = useState("en"); // "en" | "ar"

  const [stockModal, setStockModal] = useState({
    show: false,
    productName: "",
    maxQty: 0,
  });
  const [paymentStatusPopup, setPaymentStatusPopup] = useState({
    show: false,
    type: "success",
    title: "",
    message: "",
  });

  const isArabicUi =
    typeof window !== "undefined" && localStorage.getItem("lang") === "ar";
  const txt = (en, ar) => (isArabicUi ? ar : en);
  const clearStaleBackdrops = () => {
    if (typeof document === "undefined") return;
    if (document.querySelector(".modal.show")) return;
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    document.body.style.removeProperty("overflow");
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
  };
  const openPaymentStatusPopup = (type, message, title) => {
    clearStaleBackdrops();
    setPaymentStatusPopup({
      show: true,
      type,
      title:
        title ||
        (type === "success"
          ? txt("Payment Successful", "تمت عملية الدفع بنجاح")
          : txt("Payment Failed", "فشل الدفع")),
      message,
    });
  };
  const closePaymentStatusPopup = () => {
    setPaymentStatusPopup({
      show: false,
      type: "success",
      title: "",
      message: "",
    });
    setTimeout(clearStaleBackdrops, 0);
  };

  const [socialLinks, setSocialLinks] = useState({
    whatsapp: "",
    instagram: "",
    facebook: "",
    tiktok: "",
    snapchat: "",
  });
  const [reportFrom, setReportFrom] = useState(() => getDefaultReportRange().from);
  const [reportTo, setReportTo] = useState(() => getDefaultReportRange().to);
  const [reportType, setReportType] = useState(DEFAULT_REPORT_TYPE);
  const [reportLimit, setReportLimit] = useState(String(DEFAULT_REPORT_LIMIT));
  const [reportLoading, setReportLoading] = useState("");

  const labels = {
    en: {
      invoice: "Invoice",
      orderId: "Order ID",
      date: "Date",
      paymentType: "Payment Type",
      splitDetails: "Split Payment Details",
      cash: "Cash",
      card: "Card",
      cashier: "Payment Receiver",
      items: "Items",
      qty: "Qty",
      price: "Price",
      lineTotal: "Total",
      subtotal: "Subtotal",
      tax: "Tax",
      discount: "Discount",
      total: "Total",
      received: "Received",
      paying: "Paying",
      change: "Change",
      thankYou: "Thank you for your purchase!",
    },
    ar: {
      invoice: "فاتورة",
      orderId: "رقم الطلب",
      date: "التاريخ",
      paymentType: "طريقة الدفع",
      splitDetails: "تفاصيل الدفع المجزأ",
      cash: "نقداً",
      card: "بطاقة",
      cashier: "المستلم",
      items: "العناصر",
      qty: "الكمية",
      price: "السعر",
      lineTotal: "الإجمالي",
      subtotal: "الإجمالي الفرعي",
      tax: "الضريبة",
      discount: "الخصم",
      total: "الإجمالي",
      received: "المبلغ المستلم",
      paying: "المبلغ المدفوع",
      change: "الباقي",
      thankYou: "شكراً لتسوقكم!",
    },
  };

  const nf = (n, lang) =>
    (typeof n === "number" ? n : Number(n || 0)).toLocaleString(
      lang === "ar" ? "ar-SA" : "en-US",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    );

  const buildInvoiceHtml = (tx, lang = "en") => {
    const L = labels[lang] || labels.en;
    const dir = lang === "ar" ? "rtl" : "ltr";
    const alignStart = lang === "ar" ? "right" : "left";
    const alignEnd = lang === "ar" ? "left" : "right";

    // Subtotal from items (price * quantity)
    const subtotal2 = (tx.items || []).reduce(
      (sum, it) => sum + Number(it.price || 0) * Number(it.quantity || 0),
      0,
    );

    // Fallbacks from tx
    const total =
      typeof tx.totalAmount === "number" ? tx.totalAmount : subtotal2;
    const taxAmount = Number(
      tx.taxAmount ?? tx.productTaxAmount ?? tx.tax ?? 0,
    );
    const discountAmount = Math.abs(
      Number(tx.discountAmount ?? tx.discount ?? 0),
    );
    const received = tx.receivedAmount ?? 0;
    const paying = tx.payingAmount ?? total;
    const changeVal = tx.change ?? Math.max(0, received - paying);
    const splitPayments = getSplitPaymentsFromTx(tx);

    const todayStr = new Date(tx.createdAt || Date.now()).toLocaleString(
      lang === "ar" ? "ar-SA" : "en-US",
    );

    const rows = (tx.items || [])
      .map(
        (it) => `
      <tr>
        <td>${it.name || ""}</td>
        <td style="text-align:center">${nf(it.quantity || 0, lang)}</td>
        <td style="text-align:${alignEnd}">${nf(it.price || 0, lang)}</td>
        <td style="text-align:${alignEnd}">${nf(
          (it.price || 0) * (it.quantity || 0),
          lang,
        )}</td>
      </tr>`,
      )
      .join("");

    const splitRows = splitPayments
      .map((payment) => {
        const methodLabel =
          payment.method === "cash"
            ? L.cash
            : payment.method === "card"
              ? L.card
              : payment.method;
        return `
        <tr>
          <td colspan="3" class="end">${methodLabel}</td>
          <td class="end">${nf(payment.amount, lang)}</td>
        </tr>`;
      })
      .join("");

    return `
<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${L.invoice}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 16px; }
  .wrap { max-width: 360px; margin: 0 auto; }
  h1 { font-size: 18px; margin: 0 0 8px; text-align: center; }
  .meta { font-size: 12px; margin-bottom: 12px; }
  .meta div { display: flex; justify-content: space-between; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { padding: 6px 4px; border-bottom: 1px dashed #ddd; }
  th { text-align: ${alignStart}; font-weight: bold; background: #f8f8f8; }
  tfoot td { border: 0; padding: 4px 4px; }
  .total-row td { border-top: 1px solid #000; font-weight: bold; }
  .thanks { text-align: center; margin-top: 12px; font-size: 12px; }
  .end { text-align: ${alignEnd}; }
  .start { text-align: ${alignStart}; }
</style>
</head>
<body>
  <div class="wrap">
    <h1>${L.invoice} / ${lang === "ar" ? "Invoice" : "فاتورة"}</h1>

    <div class="meta">
      <div><span>${L.orderId}</span><strong>${tx.orderId || "-"}</strong></div>
      <div><span>${L.date}</span><strong>${todayStr}</strong></div>
      <div><span>${L.paymentType}</span><strong>${tx.type || "-"}</strong></div>
      <div><span>${L.cashier}</span><strong>${
        tx.paymentReceiver || "-"
      }</strong></div>
    </div>

    <table>
      <thead>
        <tr>
          <th class="start">${L.items}</th>
          <th style="text-align:center">${L.qty}</th>
          <th class="end">${L.price}</th>
          <th class="end">${L.lineTotal}</th>
        </tr>
      </thead>
      <tbody>
        ${rows || `<tr><td colspan="4" style="text-align:center">—</td></tr>`}
      </tbody>
      <tfoot>
        ${
          splitRows
            ? `<tr>
          <td colspan="4" class="start" style="font-weight:700;border-top:1px solid #000;padding-top:8px;">
            ${L.splitDetails}
          </td>
        </tr>
        ${splitRows}`
            : ""
        }
        <tr>
          <td colspan="3" class="end">${L.subtotal}</td>
          <td class="end">${nf(subtotal2, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.tax}</td>
          <td class="end">${nf(taxAmount, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.discount}</td>
          <td class="end">-${nf(discountAmount, lang)}</td>
        </tr>
        <tr class="total-row">
          <td colspan="3" class="end">${L.total}</td>
          <td class="end">${nf(total, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.received}</td>
          <td class="end">${nf(received, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.paying}</td>
          <td class="end">${nf(paying, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.change}</td>
          <td class="end">${nf(changeVal, lang)}</td>
        </tr>
      </tfoot>
    </table>

    <div class="thanks">${L.thankYou}</div>
  </div>

  <script>
    window.onload = function () {
      window.print();
      setTimeout(() => window.close(), 300);
    };
  </script>
</body>
</html>
`;
  };

  const mergeInvoiceAmounts = (baseTx = {}) => {
    const merged = { ...baseTx };
    const orderKey = String(baseTx.orderId || baseTx._id || "").trim();
    const lastTxOrderKey = String(lastTransaction?.orderId || lastTransaction?._id || "").trim();
    const sameOrderLastTx =
      orderKey && orderKey === lastTxOrderKey ? lastTransaction : null;
    const cachedSummary = getCachedPosTransactionSummary(baseTx);
    const sources = [sameOrderLastTx, cachedSummary].filter(Boolean);

    const amountKeys = [
      "subtotalAmount",
      "taxAmount",
      "discountAmount",
      "shippingAmount",
      "totalAmount",
    ];

    amountKeys.forEach((key) => {
      const current = toFiniteNumberOrNull(merged[key]);
      const fallback = sources
        .map((source) => toFiniteNumberOrNull(source?.[key]))
        .find((value) => value !== null && value !== 0);

      if (current === null && fallback !== undefined) {
        merged[key] = fallback;
        return;
      }
      if (current === 0 && fallback !== undefined) {
        merged[key] = fallback;
      }
    });

    return merged;
  };

  const printInvoice = (lang = invoiceLang, tx = lastTransaction) => {
    if (!tx) {
      alert(
        window.translate(
          "No completed transaction to print. Please place an order first.",
        ),
      );
      return;
    }
    const printableTx = mergeInvoiceAmounts(tx);
    const w = window.open("", "_blank", "width=420,height=600");
    if (!w) {
      alert(
        window.translate(
          "Popup blocked. Please allow popups to print the invoice.",
        ),
      );
      return;
    }
    w.document.open();
    w.document.write(buildInvoiceHtml(printableTx, lang));
    w.document.close();
  };

  // Fetch the categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoryData = await getAllCategories(); // try live first
        const normalized = categoryData.map((cat) => {
          const raw = cat.image || "category-default.svg";
          const isLocalAsset =
            !/^data:image/i.test(raw) &&
            !isHttpUrl(raw) &&
            !looksLikeCatalog(raw);
          const imageUrl = /^data:image/i.test(raw)
            ? raw
            : isLocalAsset
              ? `assets/img/categories/${raw}`
              : toPublicUrl(raw);
          return {
            _id: String(cat._id || cat.id || cat.CategoryId)
              .trim()
              .toLowerCase(),
            name: cat.name || cat.CategoryName || "Unnamed",
            imageUrl, // ready to render
            isLocalAsset, // tells render which component to use
          };
        });
        setCategories([
          {
            _id: "all",
            name: `${window.translate("All")}`,
            imageUrl: "assets/img/categories/category-01.svg",
            isLocalAsset: true,
          },
          ...normalized,
        ]);
        setDemo(false); // we’re live
      } catch (e) {
        if (!noStore403(e)) {
          console.error("Failed to fetch categories:", e);
          return;
        }
        // 403 because store id is missing -> demo fallback
        setDemo(true);
        const normalized = mockPos.categories.map((cat) => {
          const raw = cat.image || "category-default.svg";
          const isLocalAsset =
            !/^data:image/i.test(raw) &&
            !isHttpUrl(raw) &&
            !looksLikeCatalog(raw);
          const imageUrl = /^data:image/i.test(raw)
            ? raw
            : isLocalAsset
              ? `assets/img/categories/${raw}`
              : toPublicUrl(raw);
          return {
            _id: String(cat.id).trim().toLowerCase(),
            name: cat.name,
            imageUrl,
            isLocalAsset,
          };
        });
        setCategories([
          {
            _id: "all",
            name: `${window.translate("All")}`,
            imageUrl: "assets/img/categories/category-01.svg",
            isLocalAsset: true,
          },
          ...normalized,
        ]);
      }
    };
    loadCategories();
  }, []);

  // fetch the product
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // -     const storeId = localStorage.getItem("storeId") || localStorage.getItem("store_id");
        // -     if (demo || !storeId) throw new Error("demo-or-no-store");
        // -
        const result = await fetchProductList(); // live call
        const categoryData = await getAllCategories(); // live call
        const categoryMap = {};
        categoryData.forEach((cat) => {
          const name = (cat.name || cat.CategoryName || "")
            .toLowerCase()
            .trim();
          const id = String(cat._id || cat.id || cat.CategoryId).trim();
          categoryMap[name] = id;
        });
        const normalizedProducts = result.flatMap((p) => {
          const rawCategory =
            typeof p.Category === "object"
              ? p.Category.name || p.Category.CategoryName
              : p.Category;
          const categoryName = String(rawCategory || "").trim();

          const baseProduct = {
            ...p,
            CategoryId:
              categoryMap[String(rawCategory || "").toLowerCase().trim()] ||
              "uncategorized",
            categoryName: categoryName || "Uncategorized",
            _id: p._id || p.id || p.ProductId,
            quantity: 1,
            Price: Number(p.Price ?? p.price ?? 0),
            Qty: Number(p.Qty ?? p.quantity ?? 0),
            tax: p.tax ?? p.taxValue ?? 0,
            taxValue: p.taxValue ?? p.tax ?? 0,
            taxType: p.taxType || "",
            discountValue: p.discountValue ?? p.discount ?? 0,
            discountType: p.discountType || p.discount_type || "",
          };
          if (Array.isArray(p.variantItems) && p.variantItems.length > 0) {
            return [
              {
                ...baseProduct,
                allVariants: p.variantItems.map((v) => ({
                  ...baseProduct,
                  baseProductId: baseProduct._id,
                  _id: v._id,
                  variantId: v._id,
                  name: v.name,
                  Product_Name: `${p.Product_Name} - ${v.name}`,
                  Price: Number(v.price ?? 0),
                  Qty: Number(v.quantity ?? 0),
                  tax: v.taxValue ?? v.tax ?? 0,
                  taxValue: v.taxValue ?? v.tax ?? 0,
                  discountValue: v.discountValue ?? v.discount ?? 0,
                  discountType: v.discountType || v.discount_type || "",
                  taxType: v.taxType,
                  image: v.image || baseProduct.image,
                  isVariant: true,
                  attributes: `${p.variantAttributes?.join(", ")}: ${v.name}`,
                })),
                isParent: true,
              },
            ];
          }
          return [baseProduct];
        });
        setProducts(normalizedProducts);
        setDemo(false); // we’re live
      } catch (e) {
        // -     if (e.message === "demo-or-no-store" || isDemoActive() || noStore403(e)) {
        if (noStore403(e)) {
          setDemo(true);
          const categoryMap = {};
          mockPos.categories.forEach((c) => {
            categoryMap[c.name.toLowerCase()] = c.id;
          });
          const normalizedProducts = mockPos.products.map((p) => ({
            ...p,
            _id: p._id || p.id,
            CategoryId:
              categoryMap[String(p.Category || "").toLowerCase()] ||
              "uncategorized",
            categoryName: String(p.Category || "").trim() || "Uncategorized",
            Price: Number(p.Price ?? p.price ?? 0),
            Qty: Number(p.Qty ?? p.quantity ?? 0),
            quantity: 1,
          }));
          setProducts(normalizedProducts);
          return;
        }
        console.error("Failed to load products:", e);
      }
    };
    loadProducts();
  }, []);

  // fetch the social links
  useEffect(() => {
    const loadSocialLinks = async () => {
      const res = await getPosUserSocialLinks();
      console.log("✅ POS profileApi response:", res); // <-- add this
      if (res.success) {
        console.log("✅ socialLinks:", res.data); // <-- add this
        setSocialLinks({
          whatsapp: res.data?.whatsapp || "",
          instagram: res.data?.instagram || "",
          facebook: res.data?.facebook || "",
          tiktok: res.data?.tiktok || "",
          snapchat: res.data?.snapchat || "",
        });
      } else {
        console.warn("Social links not loaded:", res.error);
      }
    };
    loadSocialLinks();
  }, []);

  const filteredProducts = useMemo(() => {
    const byCategory =
      activeTab === "all"
        ? products
        : products.filter((p) => p.CategoryId === activeTab);

    const term = productSearch.trim().toLowerCase();
    if (!term) return byCategory;

    return byCategory.filter((p) => {
      const productName = String(p.Product_Name || p.name || "").toLowerCase();
      const categoryName = String(
        p.categoryName ||
          (typeof p.Category === "object"
            ? p.Category?.name || p.Category?.CategoryName
            : p.Category) ||
          "",
      ).toLowerCase();
      const sku = String(
        p.SKU ||
          p.sku ||
          p.productSku ||
          p.productSKU ||
          p.ProductCode ||
          p.Product_Code ||
          p.code ||
          "",
      ).toLowerCase();

      return (
        productName.includes(term) ||
        categoryName.includes(term) ||
        sku.includes(term)
      );
    });
  }, [activeTab, productSearch, products]);

  useEffect(() => {
    console.log(`📁 ActiveTab: ${activeTab}`);
    console.log("📦 Filtered Products:", filteredProducts);
  }, [activeTab, filteredProducts]);

  const publishProductAvailability = async (product) => {
    const selectedProductId = String(product?._id || product?.id || "");
    const fallbackProductId = String(product?.baseProductId || "");
    const availabilityProductId = selectedProductId || fallbackProductId;
    if (!availabilityProductId) return;

    const fallbackName =
      product?.baseProductName ||
      product?.Product_Name ||
      product?.name ||
      tr("Unnamed");

    emitPosAvailability({
      loading: true,
      error: false,
      productId: availabilityProductId,
      productName: fallbackName,
    });

    try {
      let response;
      try {
        response = await getProductById(availabilityProductId);
      } catch (primaryError) {
        if (
          fallbackProductId &&
          fallbackProductId !== availabilityProductId
        ) {
          response = await getProductById(fallbackProductId);
        } else {
          throw primaryError;
        }
      }
      const detail = response?.data || response || {};

      emitPosAvailability({
        loading: false,
        error: false,
        productId: availabilityProductId,
        productName:
          detail?.name || detail?.Product_Name || detail?.productName || fallbackName,
        data: {
          store: detail?.store || detail?.storeName || "",
          storeId: detail?.storeId || "",
          warehouse: detail?.warehouse || detail?.warehouseName || "",
          warehouseId: detail?.warehouseId || "",
          availability: Array.isArray(detail?.availability)
            ? detail.availability
            : [],
        },
      });
    } catch (error) {
      emitPosAvailability({
        loading: false,
        error: true,
        message: tr("Availability unavailable"),
        productId: availabilityProductId,
        productName: fallbackName,
      });
      console.error("Failed to fetch product availability:", error);
    }
  };

  const handleProductClick = async (product) => {
    const productId = String(product._id || product.id);
    void publishProductAvailability(product);

    const alreadyExists = selectedItems.find(
      (item) => String(item._id) === productId,
    );
    if (alreadyExists) return;

    try {
      let detailedProduct = product;

      // Only fetch details if it's NOT a variant
      if (!product.isVariant && !demo) {
        const res = await getProductById(productId);
        detailedProduct = res?.data || res;
      }

      const stock = product.isVariant
        ? Number(product.Qty ?? 0)
        : Number(detailedProduct.quantity ?? product.Qty ?? 0);

      const enriched = {
        ...product,
        _id: productId,
        Product_Name:
          product.Product_Name ||
          detailedProduct.name ||
          product.name ||
          "Unnamed",
        Price: Number(product.Price ?? detailedProduct.price ?? 0),
        quantity: 1,
        stockQty: stock,
        isVariant: Boolean(product.isVariant),
        taxType: detailedProduct.taxType || product.taxType || "",
        tax:
          detailedProduct.taxValue ??
          detailedProduct.tax ??
          product.tax ??
          product.taxValue ??
          product.taxRate ??
          product.tax_type ??
          0,
        taxValue:
          detailedProduct.taxValue ??
          detailedProduct.tax ??
          product.tax ??
          product.taxValue ??
          product.taxRate ??
          product.tax_type ??
          0,
        discountValue:
          detailedProduct.discountValue ??
          detailedProduct.discount ??
          detailedProduct.pricing?.discountValue ??
          product.discountValue ??
          product.discount ??
          product.discountRate ??
          product.discount_rate ??
          0,
        discountType:
          detailedProduct.discountType ||
          detailedProduct.pricing?.discountType ||
          product.discountType ||
          product.discount_type ||
          "",
      };

      if (!demo && stock <= 0) {
        setStockModal({
          show: true,
          productName: enriched.Product_Name,
          maxQty: 0,
        });
        return;
      }

      setSelectedItems((prev) => [...prev, enriched]);
    } catch (error) {
      console.error("Failed to fetch product details:", error);
    }
  };

  const handleQuantityChange = (id, newQty) => {
    const idStr = String(id);

    setSelectedItems((prev) => {
      const exists = prev.find((item) => String(item._id) === idStr);
      if (!exists) return prev;

      if (newQty <= 0) {
        return prev.filter((i) => String(i._id) !== idStr);
      }

      const max = Number(exists.stockQty ?? Infinity);
      if (newQty > max) {
        setStockModal({
          show: true,
          productName: exists.Product_Name,
          maxQty: max,
        });
        return prev; // stop update
      }

      return prev.map((item) =>
        String(item._id) === idStr ? { ...item, quantity: newQty } : item,
      );
    });
  };

  useEffect(() => {
    console.log("🛒 Updated selectedItems:", selectedItems);
    syncAvailabilityWithCart(selectedItems);
  }, [selectedItems]);

  const handleRemoveItem = (id) => {
    const idStr = String(id);

    setSelectedItems((prev) => {
      // Filter out the item being removed
      const newSelectedItems = prev.filter(
        (item) => String(item._id) !== idStr,
      );

      // If no items left after removal, reset the cart message
      if (newSelectedItems.length === 0) {
        console.log("🛒 Cart is empty!");
      }

      return newSelectedItems;
    });

    // Reset the quantity of the removed product in the products list
    setProducts((prev) =>
      prev.map((item) =>
        String(item._id) === idStr ? { ...item, quantity: 0 } : item,
      ),
    );

    // Ensure that the count reflects the actual number of items in the cart
    // console.log("Updated Order Details:", newSelectedItems.length);
  };

  // tax details
  const subtotal = selectedItems.reduce(
    (sum, item) => sum + item.Price * item.quantity,
    0,
  );
  //   shipping
  const shipping = 0;

  //   tax
  const tax = selectedItems.reduce((sum, item) => {
    const taxVal = getItemTaxRate(item);
    return sum + (taxVal / 100) * item.Price * item.quantity;
  }, 0);

  //   coupan
  const coupon = 0;

  //   discount
  const discount = selectedItems.reduce((sum, item) => {
    return sum + getItemDiscountAmount(item);
  }, 0);

  // roundoff
  // const roundoff = 0.11;

  const totalPayable =
    subtotal + shipping + tax - coupon - discount;
  // const totalPayable =
  //   subtotal + shipping + tax - coupon - discount + roundoff;

  //   Quantity change handler for CartCounter
  const QuantityCounter = ({ value, onChange, max = Infinity }) => {
    const handleDecrement = () => {
      if (value > 1) onChange(value - 1);
    };
    const handleIncrement = () => {
      if (value < max) onChange(value + 1);
    };
    const atMax = value >= max;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          border: "1px solid #ccc",
          borderRadius: "30px",
          overflow: "hidden",
          height: "36px",
          width: "100px",
          backgroundColor: "#fff",
        }}
        onClick={(e) => e.stopPropagation()} // prevent product card click
      >
        <button
          onClick={handleDecrement}
          style={{
            width: "32px",
            height: "100%",
            border: "none",
            backgroundColor: "#f0f0f0",
            fontSize: "18px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          −
        </button>
        <div
          style={{
            flex: 1,
            textAlign: "center",
            fontSize: "16px",
            fontWeight: "500",
            userSelect: "none",
          }}
        >
          {value}
        </div>
        <button
          onClick={handleIncrement}
          disabled={atMax}
          title={atMax ? "No more stock" : ""}
          style={{
            width: "32px",
            height: "100%",
            border: "none",
            backgroundColor: "#f0f0f0",
            fontSize: "18px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          +
        </button>
      </div>
    );
  };

  // ✅ Add prop type validation
  QuantityCounter.propTypes = {
    value: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    max: PropTypes.number,
  };

  const parseAmount = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Calculate change automatically whenever received or paying amount changes
  useEffect(() => {
    const r = parseAmount(receivedAmount);
    const p = parseAmount(payingAmount);
    const diff = r - p > 0 ? r - p : 0;
    setChange(diff.toFixed(2));
  }, [receivedAmount, payingAmount]);

  const handleReportDownload = async (format) => {
    if (reportLoading) return;
    setReportLoading(format);
    try {
      const params = buildReportParams({
        userId: localStorage.getItem("userId"),
        from: reportFrom,
        to: reportTo,
        limit: reportLimit,
        type: reportType,
      });
      const report = await getPosOverviewReport(params);
      if (!report?.success)
        throw new Error(report?.message || tr("Failed to fetch report data."));

      if (format === "excel") {
        downloadOverviewExcel(report, params);
      } else {
        downloadOverviewPdf(report, params);
      }
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          error?.message ||
          tr("Failed to download report. Please try again."),
      );
    } finally {
      setReportLoading("");
    }
  };

  // Handle Place Order submission
  const handlePlaceOrder = async (overrides = {}) => {
    try {
      const token = localStorage.getItem("token");
      const storeId = localStorage.getItem("storeId");
      if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
        openPaymentStatusPopup(
          "error",
          txt(
            "No items selected. Please add at least one product.",
            "لم يتم اختيار أي منتج. يرجى إضافة منتج واحد على الأقل.",
          ),
        );
        return false;
      }

      const validItems = selectedItems.filter(
        (item) => item && item._id && item.quantity > 0,
      );

      if (validItems.length === 0) {
        openPaymentStatusPopup(
          "error",
          txt(
            "No valid items to process.",
            "لا توجد عناصر صالحة لإتمام العملية.",
          ),
        );
        return false;
      }

      const fallbackPaymentType = {
        value: "cash",
        label: window.translate("Cash"),
      };
      const resolvedPaymentType =
        typeof overrides.paymentType === "string"
          ? { value: overrides.paymentType, label: overrides.paymentType }
          : overrides.paymentType || paymentType || fallbackPaymentType;
      const paymentTypeValue = resolvedPaymentType?.value || "cash";
      const shouldUseCollectedAmount =
        paymentTypeValue === "cash" || paymentTypeValue === "split";
      const normalizedSplitPayments = Array.isArray(overrides.splitPayments)
        ? overrides.splitPayments
            .map((payment) => {
              const method = normalizeSplitMethod(payment?.method);
              const amount = Number(payment?.amount);
              if (!method || !Number.isFinite(amount) || amount <= 0) {
                return null;
              }
              return { method, amount: Number(amount.toFixed(2)) };
            })
            .filter(Boolean)
        : [];

      const finalPayingAmount = parseFloat(
        Number(overrides.payingAmount ?? payingAmount ?? totalPayable).toFixed(
          2,
        ),
      );
      const finalReceivedAmount =
        shouldUseCollectedAmount
          ? parseFloat(
              Number(overrides.receivedAmount ?? receivedAmount ?? 0).toFixed(
                2,
              ),
            )
          : finalPayingAmount;
      const finalChange =
        overrides.change !== undefined && overrides.change !== null
          ? parseFloat(Number(overrides.change).toFixed(2))
          : shouldUseCollectedAmount
            ? parseFloat(
                Math.max(0, finalReceivedAmount - finalPayingAmount).toFixed(
                  2,
                ),
              )
            : 0;

      if (shouldUseCollectedAmount && finalReceivedAmount < finalPayingAmount) {
        openPaymentStatusPopup(
          "error",
          txt(
            "Received amount cannot be less than payable amount.",
            "المبلغ المستلم لا يمكن أن يكون أقل من المبلغ المطلوب.",
          ),
        );
        return false;
      }

      const basePaymentNote = String(paymentNote || "")
        .replace(/\s*\[split:[^\]]+\]\s*/gi, " ")
        .trim();
      const splitNoteText = normalizedSplitPayments
        .map((payment) => `${payment.method}:${payment.amount.toFixed(2)}`)
        .join("|");
      const finalPaymentNote =
        paymentTypeValue === "split" && splitNoteText
          ? [basePaymentNote, `[split:${splitNoteText}]`]
              .filter(Boolean)
              .join(" ")
          : basePaymentNote;

      const payload = {
        orderId: `ORD-${Date.now()}`,
        type: paymentTypeValue,
        receivedAmount: finalReceivedAmount,
        payingAmount: finalPayingAmount,
        change: finalChange,
        paymentReceiver,
        paymentNote: finalPaymentNote,
        saleNote,
        staffNote,
        splitPayments:
          paymentTypeValue === "split" && normalizedSplitPayments.length > 0
            ? normalizedSplitPayments
            : undefined,
        subtotalAmount: parseFloat(Number(subtotal).toFixed(2)),
        taxAmount: parseFloat(Number(tax).toFixed(2)),
        discountAmount: parseFloat(Number(discount).toFixed(2)),
        shippingAmount: parseFloat(Number(shipping).toFixed(2)),
        totalAmount: parseFloat(Number(totalPayable).toFixed(2)),
        items: validItems.map((item) => ({
          productId: item.baseProductId || item._id,
          variantId: item.isVariant ? item._id : undefined,
          name: item.Product_Name || item.name || "Unnamed",
          quantity: item.quantity,
          price: item.Price,
        })),
      };

      const res = await axios.post(
        `${API_BASE_URL}api/transactions/`,
        payload,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "X-Store-Id": storeId || undefined,
          },
          withCredentials: true,
        },
      );

      if (res.data.success) {
        const responseTx = res.data.transaction || {};
        const preferNonZeroAmount = (responseValue, fallbackValue) => {
          const fallbackNum = Number(fallbackValue ?? 0);
          const responseNum = Number(responseValue);

          if (!Number.isFinite(fallbackNum) && !Number.isFinite(responseNum)) {
            return 0;
          }
          if (!Number.isFinite(fallbackNum)) return responseNum;
          if (!Number.isFinite(responseNum)) return fallbackNum;

          // Some APIs send 0 for these fields; keep frontend-computed value when it is non-zero.
          if (responseNum === 0 && fallbackNum !== 0) return fallbackNum;
          return responseNum;
        };
        const tx = {
          ...responseTx,
          type: responseTx.type || payload.type,
          paymentNote: responseTx.paymentNote ?? finalPaymentNote,
          splitPayments:
            Array.isArray(responseTx.splitPayments) &&
            responseTx.splitPayments.length > 0
              ? responseTx.splitPayments
              : normalizedSplitPayments,
          subtotalAmount: preferNonZeroAmount(
            responseTx.subtotalAmount,
            payload.subtotalAmount,
          ),
          taxAmount: preferNonZeroAmount(responseTx.taxAmount, payload.taxAmount),
          discountAmount: preferNonZeroAmount(
            responseTx.discountAmount,
            payload.discountAmount,
          ),
          shippingAmount: preferNonZeroAmount(
            responseTx.shippingAmount,
            payload.shippingAmount,
          ),
        };
        cachePosTransactionSummary(tx);
        setLastTransaction(tx);
        openPaymentStatusPopup(
          "success",
          txt("Transaction created successfully!", "تم إنشاء العملية بنجاح!"),
        );
        setSelectedItems([]);
        setReceivedAmount("");
        setPayingAmount(0);
        setChange("0.00");
        setPaymentReceiver("");
        setPaymentNote("");
        setSaleNote("");
        setStaffNote("");
        setPaymentType({ value: "cash", label: "Cash" });
        return true;
      }

      openPaymentStatusPopup(
        "error",
        txt("Failed to submit transaction.", "تعذر إرسال العملية. حاول مرة أخرى."),
      );
      return false;
    } catch (err) {
      console.error("Transaction Error:", err);
      openPaymentStatusPopup(
        "error",
        txt("Failed to submit transaction.", "تعذر إرسال العملية. حاول مرة أخرى."),
      );
      return false;
    }
  };

  // Render product card
  const renderProductCard = (product) => {
    const isSelected = selectedItems.some(
      (item) => String(item._id) === String(product._id),
    );

    return (
      <div
        key={product._id}
        className="col-sm-6 col-md-6 col-lg-6 col-xl-4 col-xxl-3"
      >
        <div
          className={`product-info card mb-0 ${isSelected ? "active" : ""}`}
          data-product-id={product._id}
          onClick={() => handleProductClick(product)}
          tabIndex="0"
        >
          <Link to="#" className="pro-img">
            <img
              src={product.image}
              style={{ width: "160px", height: "160px", objectFit: "cover" }}
              alt={product.Product_Name}
              onError={(e) => (e.target.src = "/assets/img/products/shoes.jpg")}
            />
            <span>
              <i className="ti ti-circle-check-filled" />
            </span>
          </Link>

          <h6 className="cat-name">
            <Link to="#">{product.Category || "Uncategorized"}</Link>
          </h6>

          <h6 className="product-name">
            <Link to="#">
              {product.Product_Name || product.name}
              {/* {product.isVariant && product.attributes ? (
                <small
                  style={{ display: "block", fontSize: "12px", color: "#888" }}
                >
                  {product.attributes}
                </small>
              ) : null} */}
            </Link>
          </h6>

          {product.allVariants?.length > 0 && (
            <div className="variant-buttons d-flex flex-wrap gap-1 mb-2 px-2">
              {product.allVariants.map((variant) => {
                const isSelected = selectedItems.some(
                  (item) => String(item._id) === String(variant._id),
                );

                return (
                  <button
                    key={variant._id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProductClick(variant);
                    }}
                    className={`btn btn-sm px-3 py-1 ${
                      isSelected
                        ? "btn-primary text-white"
                        : "btn-outline-secondary"
                    }`}
                    style={{
                      borderRadius: "20px",
                      fontSize: "12px",
                      textTransform: "capitalize",
                    }}
                  >
                    {variant.name}
                  </button>
                );
              })}
            </div>
          )}

          <div className="d-flex align-items-center justify-content-between price px-2">
            <p className="text-gray-9 mb-0">
              {Number(product.Price || 0).toFixed(2)}
            </p>
            <div className="qty-item m-0">
              {/* <QuantityCounter
                value={
                  selectedItems.find(
                    (item) => String(item._id) === String(product._id)
                  )?.quantity ?? 1
                }
                onChange={(newQty) => handleQuantityChange(product._id, newQty)}
              /> */}
              <QuantityCounter
                value={
                  selectedItems.find(
                    (item) => String(item._id) === String(product._id),
                  )?.quantity ?? 1
                }
                max={
                  selectedItems.find(
                    (item) => String(item._id) === String(product._id),
                  )?.stockQty ?? Infinity
                }
                onChange={(newQty) => handleQuantityChange(product._id, newQty)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add helpers to build URLs safely
  const ensureHttp = (url) => {
    if (!url) return "";
    const v = String(url).trim();
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v;
    // allow "instagram.com/..." etc
    if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(v)) return `https://${v}`;
    return v;
  };

  const buildWhatsAppLink = (value) => {
    if (!value) return "";
    const v = String(value).trim();
    if (!v) return "";
    // if already URL
    if (/^https?:\/\//i.test(v)) return v;

    // if phone number -> convert to wa.me
    const digits = v.replace(/[^\d]/g, "");
    if (digits) return `https://wa.me/${digits}`;
    return "";
  };

  const SocialLinksBar = () => {
    const items = [
      {
        key: "whatsapp",
        label: "WhatsApp",
        icon: MessageCircle,
        href: buildWhatsAppLink(socialLinks.whatsapp),
      },
      {
        key: "instagram",
        label: "Instagram",
        icon: Instagram,
        href: ensureHttp(socialLinks.instagram),
      },
      {
        key: "facebook",
        label: "Facebook",
        icon: Facebook,
        href: ensureHttp(socialLinks.facebook),
      },
      {
        key: "tiktok",
        label: "TikTok",
        icon: Music2,
        href: ensureHttp(socialLinks.tiktok),
      },
      {
        key: "snapchat",
        label: "Snapchat",
        icon: Ghost,
        href: ensureHttp(socialLinks.snapchat),
      },
    ];

    return (
      <div className="d-flex align-items-center gap-2 flex-wrap">
        {items.map((it) => {
          const Icon = it.icon;
          const disabled = !it.href;

          return disabled ? (
            <button
              key={it.key}
              type="button"
              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2"
              style={{ borderRadius: 999, opacity: 0.4, cursor: "not-allowed" }}
              title={`${it.label} (not set)`}
              disabled
            >
              <Icon size={16} />
              <span className="d-none d-md-inline">{it.label}</span>
            </button>
          ) : (
            <a
              key={it.key}
              href={it.href}
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2"
              title={it.label}
              style={{ borderRadius: 999 }}
            >
              <Icon size={16} />
              <span className="d-none d-md-inline">{it.label}</span>
              <ExternalLink size={14} />
            </a>
          );
        })}
      </div>
    );
  };

  return (
    <div className="main-wrapper pos-five">
      <div className="page-wrapper pos-pg-wrapper me-0 ms-0">
        <div className="content pos-design p-0">
          <div className="row pos-wrapper">
            {/* Products */}
            <div className="col-md-12 col-lg-7 col-xl-8 d-flex">
              <div className="pos-categories tabs_wrapper p-0 flex-fill">
                <div className="content-wrap">
                  <div className="tab-wrap">
                    <ul className="tabs owl-carousel pos-category5">
                      {categories.map((cat) => (
                        <li
                          key={cat._id}
                          onClick={() => {
                            console.log(`🟡 Category Selected: ${cat._id}`);
                            setActiveTab(cat._id);
                          }}
                          className={activeTab === cat._id ? "active" : ""}
                        >
                          <Link to="#">
                            {cat.isLocalAsset ? (
                              <ImageWithBasePath
                                src={cat.imageUrl} // e.g. assets/img/categories/category-01.svg
                                alt="Category"
                              />
                            ) : (
                              <img
                                src={cat.imageUrl} // S3/http(s)/data URL
                                alt="Category"
                                onError={(e) =>
                                  (e.currentTarget.src =
                                    "/assets/img/categories/default.svg")
                                }
                              />
                            )}
                          </Link>
                          <h6>
                            <Link to="#">{cat.name}</Link>
                          </h6>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="tab-content-wrap">
                    <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                      <div className="mb-3">
                        <h5 className="mb-1">{window.translate("Welcome")}</h5>
                        <p>
                          {new Date().toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                      </div>

                      {/* ✅ Social Links */}
                      <div className="mb-3">
                        <SocialLinksBar />
                      </div>
                    </div>
                    <div className="card border mb-3">
                      <div className="card-body py-3">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                          <h6 className="mb-0">{tr("Reports")}</h6>
                        </div>
                        <div className="row g-2 align-items-end">
                          <div className="col-6 col-md-3">
                            <label className="form-label mb-1">{tr("From")}</label>
                            <input
                              type="date"
                              className="form-control"
                              value={reportFrom}
                              max={reportTo || undefined}
                              onChange={(event) => setReportFrom(event.target.value)}
                            />
                          </div>
                          <div className="col-6 col-md-3">
                            <label className="form-label mb-1">{tr("To")}</label>
                            <input
                              type="date"
                              className="form-control"
                              value={reportTo}
                              min={reportFrom || undefined}
                              onChange={(event) => setReportTo(event.target.value)}
                            />
                          </div>
                          <div className="col-6 col-md-2">
                            <label className="form-label mb-1">{tr("Type")}</label>
                            <select
                              className="form-select"
                              value={reportType}
                              onChange={(event) => setReportType(event.target.value)}
                            >
                              <option value="cash">{tr("Cash")}</option>
                              <option value="card">{tr("Card")}</option>
                              <option value="all">{tr("All")}</option>
                            </select>
                          </div>
                          <div className="col-6 col-md-2">
                            <label className="form-label mb-1">{tr("Limits")}</label>
                            <select
                              className="form-select"
                              value={reportLimit}
                              onChange={(event) =>
                                setReportLimit(event.target.value)
                              }
                            >
                              <option value="all">{tr("All")}</option>
                              {[10, 25, 50, 100, 200, 500].map((limitOption) => (
                                <option key={limitOption} value={limitOption}>
                                  {limitOption}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-12 col-sm-6 col-lg-2">
                            <div className="d-flex gap-2 flex-wrap justify-content-lg-end">
                              <button
                                type="button"
                                className="btn btn-danger d-inline-flex align-items-center justify-content-center"
                                disabled={!!reportLoading}
                                onClick={() => handleReportDownload("pdf")}
                                title={tr("Download PDF")}
                                aria-label={tr("Download PDF")}
                                style={{
                                  minWidth: 46,
                                  minHeight: 42,
                                  flex: "1 1 calc(50% - 4px)",
                                }}
                              >
                                {reportLoading === "pdf" ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <Printer size={18} />
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-success d-inline-flex align-items-center justify-content-center"
                                disabled={!!reportLoading}
                                onClick={() => handleReportDownload("excel")}
                                title={tr("Download Excel")}
                                aria-label={tr("Download Excel")}
                                style={{
                                  minWidth: 46,
                                  minHeight: 42,
                                  flex: "1 1 calc(50% - 4px)",
                                }}
                              >
                                {reportLoading === "excel" ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <FileSpreadsheet size={18} />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="pos-product-search mb-3">
                      <div className="pos-product-search__field">
                        <Search
                          size={17}
                          className="pos-product-search__icon"
                          aria-hidden="true"
                        />
                        <input
                          type="search"
                          value={productSearch}
                          onChange={(event) =>
                            setProductSearch(event.target.value)
                          }
                          className="form-control pos-product-search__input"
                          placeholder={tr("Search by name, category or SKU")}
                          aria-label={tr("Search products")}
                        />
                        {productSearch ? (
                          <button
                            type="button"
                            className="pos-product-search__clear"
                            onClick={() => setProductSearch("")}
                            aria-label={tr("Clear search")}
                          >
                            <X size={14} />
                          </button>
                        ) : null}
                      </div>
                      <small className="text-muted">
                        {filteredProducts.length}{" "}
                        {filteredProducts.length === 1
                          ? tr("product found")
                          : tr("products found")}
                      </small>
                    </div>
                    <div className="pos-products">
                      <div className="tabs_container">
                        <div
                          className={`tab_content ${activeTab} active`}
                          data-tab={activeTab}
                        >
                          <div className="row g-3">
                            {filteredProducts.length > 0 ? (
                              filteredProducts.map(renderProductCard)
                            ) : (
                              <div className="col-12">
                                <p className="text-muted fs-3">
                                  {productSearch
                                    ? window.translate(
                                        "No products match your search",
                                      )
                                    : window.translate(
                                        "No products found in this category",
                                      )}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* /Products */}

            {/* Order Details */}
            <div className="col-md-12 col-lg-5 col-xl-4 ps-0 theiaStickySidebar d-lg-flex">
              <aside className="product-order-list bg-secondary-transparent flex-fill">
                <div className="card">
                  <div className="card-body">
                    <div className="order-head d-flex align-items-center justify-content-between w-100">
                      <div>
                        <h3>{window.translate("Order List")}</h3>
                      </div>
                      {/* <div className="d-flex align-items-center gap-2">
                        <span className="badge badge-dark fs-10 fw-medium badge-xs">
                          #ORD123
                        </span>
                        <Link className="link-danger fs-16" to="#">
                          <i className="ti ti-trash-x-filled" />
                        </Link>
                      </div> */}
                    </div>
                    {/* <div className="customer-info block-section">
                      <h5 className="mb-2">Customer Information</h5>
                      <div className="d-flex align-items-center gap-2">
                        <div className="flex-grow-1">
                          <Select
                            options={options}
                            classNamePrefix="react-select select"
                            placeholder="Choose a Name"
                            defaultValue={options[0]}
                            onChange={(selected) =>
                              setCustomerId(selected?.value)
                            }
                          />
                        </div>
                        <Link
                          to="#"
                          className="btn btn-teal btn-icon fs-20"
                          data-bs-toggle="modal"
                          data-bs-target="#create"
                        >
                          <i className="ti ti-user-plus" />
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-info btn-icon fs-20"
                          data-bs-toggle="modal"
                          data-bs-target="#barcode"
                        >
                          <i className="ti ti-scan" />
                        </Link>
                      </div>
                      {showAlert && (
                        <div className="customer-item border border-orange bg-orange-100 d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
                          <div>
                            <h6 className="fs-16 fw-bold mb-1">
                              James Anderson
                            </h6>
                            <div className="d-inline-flex align-items-center gap-2 customer-bonus">
                              <p className="fs-13 d-inline-flex align-items-center gap-1">
                                Bonus :
                                <span className="badge bg-cyan fs-13 fw-bold p-1">
                                  148
                                </span>{" "}
                              </p>
                              <p className="fs-13 d-inline-flex align-items-center gap-1">
                                Loyality :
                                <span className="badge bg-teal fs-13 fw-bold p-1">
                                  $20
                                </span>{" "}
                              </p>
                            </div>
                          </div>
                          <Link to="#" className="btn btn-orange btn-sm">
                            Apply
                          </Link>
                          <Link
                            to="#"
                            className="close-icon"
                            onClick={() => setShowAlert(false)}
                          >
                            <i className="ti ti-x" />
                          </Link>
                        </div>
                      )}
                    </div> */}
                    <div className="product-added block-section">
                      <div className="head-text d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <h5 className="me-2">
                            {window.translate("Order Details")}
                          </h5>
                          <div className="badge bg-light text-gray-9 fs-12 fw-semibold py-2 border rounded">
                            {window.translate("Items")} :{" "}
                            <span className="text-teal">
                              {selectedItems.length}
                            </span>
                          </div>
                        </div>
                        {/* <Link
                          to="#"
                          className="d-flex align-items-center clear-icon fs-10 fw-medium"
                        >
                          Clear all
                        </Link> */}
                      </div>
                      <div className="product-wrap">
                        <div className="empty-cart">
                          <div className="fs-24 mb-1">
                            <i className="ti ti-shopping-cart" />
                          </div>
                          <p className="fw-bold">
                            {window.translate("No Products Selected")}
                          </p>
                        </div>
                        <div className="product-list border-0 p-0">
                          <div className="table-responsive">
                            <table className="table table-borderless">
                              <thead>
                                <tr>
                                  <th className="fw-bold bg-light">
                                    {window.translate("Item")}
                                  </th>
                                  <th className="fw-bold bg-light">
                                    {window.translate("QTY")}
                                  </th>
                                  <th className="fw-bold bg-light text-end">
                                    {window.translate("Cost")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedItems.map((item) => (
                                  <tr key={`${item._id}-${item.quantity}`}>
                                    {console.log(
                                      "🧾 Rendered Order Row:",
                                      item._id,
                                      "| Qty:",
                                      item.quantity,
                                      "| Unit Price: $",
                                      item.Price,
                                      "| Total: $",
                                      (item.Price * item.quantity).toFixed(2),
                                    )}
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <Link
                                          className="delete-icon"
                                          to="#"
                                          onClick={() =>
                                            handleRemoveItem(item._id)
                                          }
                                        >
                                          <i className="ti ti-trash-x-filled" />
                                        </Link>
                                        <h6 className="fs-13 fw-normal">
                                          <Link to="#" className="link-default">
                                            {item.Product_Name || item.name}
                                          </Link>
                                        </h6>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="qty-item m-0">
                                        <td>
                                          <div className="qty-item m-0">
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                border: "1px solid #ccc",
                                                borderRadius: "30px",
                                                overflow: "hidden",
                                                height: "36px",
                                                width: "100px",
                                                backgroundColor: "#fff",
                                              }}
                                            >
                                              <button
                                                onClick={() => {
                                                  if (item.quantity > 1) {
                                                    handleQuantityChange(
                                                      item._id,
                                                      item.quantity - 1,
                                                    );
                                                  }
                                                }}
                                                style={{
                                                  width: "32px",
                                                  height: "100%",
                                                  border: "none",
                                                  backgroundColor: "#f0f0f0",
                                                  fontSize: "18px",
                                                  fontWeight: "bold",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                −
                                              </button>
                                              <div
                                                style={{
                                                  flex: 1,
                                                  textAlign: "center",
                                                  fontSize: "16px",
                                                  fontWeight: "500",
                                                  userSelect: "none",
                                                }}
                                              >
                                                {item.quantity}
                                              </div>
                                              <button
                                                onClick={() => {
                                                  handleQuantityChange(
                                                    item._id,
                                                    item.quantity + 1,
                                                  );
                                                }}
                                                style={{
                                                  width: "32px",
                                                  height: "100%",
                                                  border: "none",
                                                  backgroundColor: "#f0f0f0",
                                                  fontSize: "18px",
                                                  fontWeight: "bold",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                +
                                              </button>
                                            </div>
                                          </div>
                                        </td>
                                      </div>
                                    </td>
                                    <td className="fs-13 fw-semibold text-gray-9 text-end">
                                      {(item.Price * item.quantity).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      {/* <div className="discount-item d-flex align-items-center justify-content-between  bg-purple-transparent mt-3 flex-wrap gap-2">
                        <div className="d-flex align-items-center">
                          <span className="bg-purple discount-icon br-5 flex-shrink-0 me-2">
                            <img
                              src="assets/img/icons/discount-icon.svg"
                              alt="img"
                            />
                          </span>
                          <div>
                            <h6 className="fs-14 fw-bold text-purple mb-1">
                              Discount 5%
                            </h6>
                            <p className="mb-0">
                              For $20 Minimum Purchase, all Items
                            </p>
                            <p></p>
                          </div>
                        </div>
                        <Link to="#" className="close-icon">
                          <i className="ti ti-trash" />
                        </Link>
                      </div> */}
                    </div>
                    <div className="order-total bg-total bg-white p-0">
                      <h5 className="mb-3">
                        {window.translate("Payment Summary")}
                      </h5>
                      {/* <table className="table table-responsive table-borderless">
                        <tbody>
                          <tr>
                            <td>
                              Shipping
                             
                            </td>
                            <td className="text-gray-9 text-end">
                              ${shipping.toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              Tax
                              
                            </td>
                            <td className="text-gray-9 text-end">
                              ${tax.toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              Coupon
                              
                            </td>
                            <td className="text-gray-9 text-end">
                              ${coupon.toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <span className="text-danger">Discount</span>
                             
                            </td>
                            <td className="text-danger text-end">
                              ${discount.toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <div className="form-check form-switch">
                                
                                <label
                                  className="form-check-label"
                                  htmlFor="round"
                                >
                                  Roundoff
                                </label>
                              </div>
                            </td>
                            <td className="text-gray-9 text-end">
                              +{roundoff.toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>Sub Total</td>
                            <td className="text-gray-9 text-end">
                              ${subtotal.toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold border-top border-dashed">
                              Total Payable
                            </td>
                            <td className="text-gray-9 fw-bold text-end border-top border-dashed">
                              ${totalPayable.toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table> */}
                      {selectedItems.length > 0 && (
                        <table className="table table-responsive table-borderless">
                          <tbody>
                            {/* <tr>
                              <td>{window.translate("Shipping")}</td>
                              <td className="text-gray-9 text-end">
                                {shipping.toFixed(2)}
                              </td>
                            </tr> */}
                            <tr>
                              <td>{window.translate("Tax")}</td>
                              <td className="text-gray-9 text-end">
                                {tax.toFixed(2)}
                              </td>
                            </tr>
                            {/* <tr>
                              <td>{window.translate("Coupon")}</td>
                              <td className="text-gray-9 text-end">
                                {coupon.toFixed(2)}
                              </td>
                            </tr> */}
                            <tr>
                              <td>
                                <span className="text-danger">
                                  {window.translate("Discount")}
                                </span>
                              </td>
                              <td className="text-danger text-end">
                                {discount.toFixed(2)}
                              </td>
                            </tr>
                            {/* <tr>
                              <td>
                                <div className="form-check form-switch p-0">
                                  <label
                                    className="form-check-label"
                                    htmlFor="round"
                                  >
                                    {window.translate("Roundoff")}
                                  </label>
                                </div>
                              </td>
                              <td className="text-gray-9 text-end">
                                +{roundoff.toFixed(2)}
                              </td>
                            </tr> */}
                            <tr>
                              <td>{window.translate("Sub Total")}</td>
                              <td className="text-gray-9 text-end">
                                {subtotal.toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold border-top border-dashed">
                                {window.translate("Total Payable")}
                              </td>
                              <td className="text-gray-9 fw-bold text-end border-top border-dashed">
                                {totalPayable.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
                <div className="card payment-method">
                  <div className="card-body">
                    <h5 className="mb-3">
                      {window.translate("Select Payment")}
                    </h5>
                    <div className="row align-items-center methods g-2">
                      <div className="col-sm-6 col-md-4 d-flex">
                        <Link
                          to="#"
                          className={`payment-item d-flex align-items-center justify-content-center p-2 flex-fill ${
                            paymentType?.value === "cash" ? "active" : ""
                          }`}
                          data-bs-toggle="modal"
                          data-bs-target="#payment-cash"
                          onClick={(e) => {
                            e.preventDefault();
                            setPaymentType({
                              value: "cash",
                              label: window.translate("Cash"),
                            });
                          }}
                        >
                          <img
                            src="assets/img/icons/cash-icon.svg"
                            className="me-2"
                            alt="img"
                          />
                          <p className="fs-14 fw-medium">
                            {window.translate("Cash")}
                          </p>
                        </Link>
                      </div>
                      <div className="col-sm-6 col-md-4 d-flex">
                        <Link
                          to="#"
                          className={`payment-item d-flex align-items-center justify-content-center p-2 flex-fill ${
                            paymentType?.value === "card" ? "active" : ""
                          }`}
                          data-bs-toggle="modal"
                          data-bs-target="#payment-card"
                          onClick={(e) => {
                            e.preventDefault();
                            setPaymentType({
                              value: "card",
                              label: window.translate("Debit / Credit Card"),
                            });
                          }}
                        >
                          <img
                            src="assets/img/icons/card.svg"
                            className="me-2"
                            alt="img"
                          />
                          <p className="fs-14 fw-medium">
                            {window.translate("Debit / Credit Card")}
                          </p>
                        </Link>
                      </div>

                      <div className="col-sm-6 col-md-4 d-flex">
                        <Link
                          to="#"
                          className={`payment-item d-flex align-items-center justify-content-center p-2 flex-fill ${
                            paymentType?.value === "split" ? "active" : ""
                          }`}
                          data-bs-toggle="modal"
                          data-bs-target="#split-payment"
                          onClick={(e) => {
                            e.preventDefault();
                            setPaymentType({
                              value: "split",
                              label: window.translate("Split Payment"),
                            });
                          }}
                        >
                          <img
                            src="assets/img/icons/split-bill.svg"
                            className="me-2"
                            alt="img"
                          />
                          <p className="fs-14 fw-medium">
                            {window.translate("Split Payment")}
                          </p>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="btn-row d-flex flex-column gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <select
                      className="form-select pd-3"
                      style={{
                        paddingTop: 10,
                        paddingBottom: 10,
                        flex: "1 1 0",
                        minWidth: 0,
                      }}
                      value={invoiceLang}
                      onChange={(e) => setInvoiceLang(e.target.value)}
                    >
                      <option value="en">
                        {window.translate("English Invoice")}
                      </option>
                      <option value="ar">
                        {window.translate("Arabic Invoice")}
                      </option>
                    </select>

                    <button
                      type="button"
                      className="btn btn-white d-flex align-items-center justify-content-center m-0"
                      onClick={() => printInvoice(invoiceLang)}
                      title={window.translate("Print last successful invoice")}
                      style={{ flex: "1 1 0", minWidth: 0 }}
                    >
                      <i className="ti ti-printer me-2 ms-2" />
                      {window.translate("Print Order")}
                    </button>
                  </div>

                  <Link
                    to="#"
                    className="btn btn-secondary d-flex align-items-center justify-content-center m-0 w-100"
                    onClick={handlePlaceOrder}
                  >
                    <i className="ti ti-shopping-cart me-2 ms-2" />
                    {window.translate("Place Order")}
                  </Link>

                  <button
                    type="button"
                    className="btn btn-white d-flex align-items-center justify-content-center m-0 w-100"
                    data-bs-toggle="modal"
                    data-bs-target="#recents"
                  >
                    <i className="ti ti-history me-2 ms-2" />
                    {window.translate("Recent Transactions")}
                  </button>
                </div>
              </aside>
            </div>
            {/* /Order Details */}
          </div>
          {/* <div className="pos-footer bg-white p-3 border-top">
            <div className="d-flex align-items-center justify-content-center flex-wrap gap-2">
              <Link
                to="#"
                className="btn btn-orange d-inline-flex align-items-center justify-content-center"
                data-bs-toggle="modal"
                data-bs-target="#hold-order"
              >
                <i className="ti ti-player-pause me-2" />
                Hold
              </Link>
              <Link
                to="#"
                className="btn btn-info d-inline-flex align-items-center justify-content-center"
              >
                <i className="ti ti-trash me-2" />
                Void
              </Link>
              <Link
                to="#"
                className="btn btn-cyan d-flex align-items-center justify-content-center"
                data-bs-toggle="modal"
                data-bs-target="#payment-completed"
              >
                <i className="ti ti-cash-banknote me-2" />
                Payment
              </Link>
              <Link
                to="#"
                className="btn btn-secondary d-inline-flex align-items-center justify-content-center"
                data-bs-toggle="modal"
                data-bs-target="#orders"
              >
                <i className="ti ti-shopping-cart me-2" />
                View Orders
              </Link>
              <Link
                to="#"
                className="btn btn-indigo d-inline-flex align-items-center justify-content-center"
                data-bs-toggle="modal"
                data-bs-target="#reset"
              >
                <i className="ti ti-reload me-2" />
                Reset
              </Link>
              <Link
                to="#"
                className="btn btn-danger d-inline-flex align-items-center justify-content-center"
                data-bs-toggle="modal"
                data-bs-target="#recents"
              >
                <i className="ti ti-refresh-dot me-2" />
                Transaction
              </Link>
            </div>
          </div> */}
        </div>
      </div>

      {/* Stock Warning Modal */}
      {stockModal.show && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="ti ti-alert-triangle me-2" />
                  Stock Warning
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() =>
                    setStockModal({ show: false, productName: "", maxQty: 0 })
                  }
                />
              </div>

              <div className="modal-body text-center py-4">
                {stockModal.maxQty === 0 ? (
                  <>
                    <h5 className="text-danger mb-2">
                      {stockModal.productName}
                    </h5>
                    <p className="mb-0">This item is currently out of stock.</p>
                  </>
                ) : (
                  <>
                    <h5 className="mb-2">{stockModal.productName}</h5>
                    <p className="mb-0">
                      Only <strong>{stockModal.maxQty}</strong> item(s)
                      available in stock.
                    </p>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setStockModal({ show: false, productName: "", maxQty: 0 })
                  }
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {paymentStatusPopup.show && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closePaymentStatusPopup}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2200,
            background: "rgba(15, 23, 42, 0.5)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            dir={isArabicUi ? "rtl" : "ltr"}
            style={{
              width: "100%",
              maxWidth: "460px",
              background: "#fff",
              borderRadius: "14px",
              boxShadow: "0 24px 56px rgba(15,23,42,0.22)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "#fff",
                background:
                  paymentStatusPopup.type === "success"
                    ? "linear-gradient(90deg,#10b981,#34d399)"
                    : "linear-gradient(90deg,#ef4444,#f87171)",
              }}
            >
              <h5 style={{ margin: 0, fontWeight: 700 }}>
                {paymentStatusPopup.title}
              </h5>
              <button
                type="button"
                onClick={closePaymentStatusPopup}
                aria-label={txt("Close", "إغلاق")}
                style={{
                  border: "none",
                  background: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  width: "30px",
                  height: "30px",
                  borderRadius: "999px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: "20px 16px 10px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "999px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      paymentStatusPopup.type === "success"
                        ? "rgba(16,185,129,0.14)"
                        : "rgba(239,68,68,0.14)",
                    color:
                      paymentStatusPopup.type === "success"
                        ? "#059669"
                        : "#dc2626",
                    flexShrink: 0,
                  }}
                >
                  {paymentStatusPopup.type === "success" ? (
                    <CheckCircle2 size={22} />
                  ) : (
                    <AlertCircle size={22} />
                  )}
                </div>
                <p className="mb-0 fw-medium" style={{ lineHeight: 1.6 }}>
                  {paymentStatusPopup.message}
                </p>
              </div>
            </div>
            <div
              style={{
                padding: "12px 16px 16px",
                display: "flex",
                justifyContent: isArabicUi ? "flex-start" : "flex-end",
              }}
            >
              <button
                type="button"
                className="btn btn-primary"
                onClick={closePaymentStatusPopup}
              >
                {txt("OK", "حسناً")}
              </button>
            </div>
          </div>
        </div>
      )}

      <PosModals
        selectedItems={selectedItems}
        totalAmount={totalPayable}
        receivedAmount={receivedAmount}
        setReceivedAmount={setReceivedAmount}
        payingAmount={payingAmount}
        setPayingAmount={setPayingAmount}
        change={change}
        setChange={setChange}
        paymentType={paymentType}
        setPaymentType={setPaymentType}
        paymentReceiver={paymentReceiver}
        setPaymentReceiver={setPaymentReceiver}
        paymentNote={paymentNote}
        setPaymentNote={setPaymentNote}
        saleNote={saleNote}
        setSaleNote={setSaleNote}
        staffNote={staffNote}
        setStaffNote={setStaffNote}
        handlePlaceOrder={handlePlaceOrder}
        invoiceLang={invoiceLang}
        onPrintRecentTransaction={printInvoice}
      />
    </div>
  );
};

export default Pos;

